import DeviceHistory from 'features/Integrations/components/DeviceHistory/DeviceHistory';
import NodeStatus from 'features/Integrations/components/NodeStatus/NodeStatus';
import DeviceControls from 'features/Integrations/components/DeviceControls/DeviceControls';
import BaseModal from 'components/Modals/BaseModal';
// deno-lint-ignore import-prefix-missing
import ConfirmModal from 'components/UI/ConfirmPopup';
import { usePerms } from 'contexts/PermsContext';
import layout from 'data/DeviceControls.json';
// deno-lint-ignore import-prefix-missing
import { USGS_PROVISIONAL_STATEMENT } from 'features/Integrations/features/ConnectedDevices/assets/USGSProvisionalStatment';
import DeviceMap from 'features/Integrations/features/ConnectedDevices/features/DeviceMap/index.web.jsx';
import { getGroupMapBounds } from 'features/Integrations/features/ConnectedDevices/features/DeviceMap/utils/deviceMap.utils';
import { hasDevicePermissionFromDevice } from 'features/Integrations/features/ConnectedSystems/utils/devicePermissions.utils';
import {
  openStageSettings,
  openDeviceSettings,
  openUSGSSettings,
} from 'features/Integrations/state/slices/deviceSlice';
import { DeviceIsSystemType } from 'features/Integrations/utils/deviceComparison.utils';
import {
  defineGroupType,
  isDeviceTypeAllowed,
} from 'features/Integrations/utils/deviceGroupRules.utils';
import useDeviceSeriesData from 'features/Integrations/hooks/useDeviceSeriesData';
import { useCallback, useEffect, useMemo, useState } from 'react';
// deno-lint-ignore import-prefix-missing
import { FaCog } from 'react-icons/fa';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { Accordion } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { sendGroupCommand } from 'services/devices/commands/commands.service';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import {
  clampPanelHeight,
  DragResizeHandle,
  readStoredPanelHeight,
  storePanelHeight,
} from 'utils/layout/panelResize.utils';
import {
  selectUsgsProvisonalAccepted,
  setUsgsProvisonalAccepted,
} from 'store/slices/Widget/widgetSlice.js';
import GroupDevicesAccordion from './components/GroupDevicesAccordion/GroupDevicesAccordion';
import GroupDevicesTable from './components/GroupDevicesTable/GroupDevicesTable';
import GroupSirenAudioRows from './components/GroupSirenAudioRows/GroupSirenAudioRows';
import {
  buildGroupCommandArgs,
  buildGroupHistoryRows,
  buildGroupCommandDraft,
  isSirenDevice,
  resolveGroupDevices,
} from '../../utils/settings.utils';
import {
  DEVICE_HISTORY_PAGE_SIZE,
  DEVICE_HISTORY_START_MS,
} from 'features/Integrations/config/deviceHistory.config';
import useGroupCommandDraft from '../../hooks/useGroupCommandDraft';

/** The map panel: the same drag handle and remembered height as the siren modal's media panel. */
const MAP_PANEL_HEIGHT_KEY = 'groupSettings.mapPanelHeight';
const MAP_PANEL_DEFAULT_PX = 360;

const GroupDeviceSettingsModal = ({ show, onClose, group }) => {
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery();
  const deviceSource = connectedDevices;
  const dispatch = useDispatch();
  const perms = usePerms();

  // Resolve the actual device objects for the selected group.
  const groupDevices = useMemo(
    () => resolveGroupDevices(deviceSource, group),
    [deviceSource, group],
  );
  // The history table lists each member's command events over time, and the
  // page load holds ONE row for each device. Fetch the newest rows for the
  // members when the modal opens; they merge into the device cache the table
  // reads. See DEVICE_HISTORY_START_MS for why the window starts at the epoch.
  const groupDeviceIds = useMemo(() => groupDevices.map((device) => device.id), [groupDevices]);
  const {
    isFetching: historyLoading,
    hasMore: historyHasMore,
    loadMore: loadMoreHistory,
    error: historyError,
  } = useDeviceSeriesData({
    ids: groupDeviceIds,
    startTime: DEVICE_HISTORY_START_MS,
    limit: DEVICE_HISTORY_PAGE_SIZE,
    enabled: Boolean(show) && groupDeviceIds.length > 0,
  });
  // Resolve the effective group type based on current membership (may differ from stored type).
  const groupType = useMemo(
    () => defineGroupType(group?.devices, deviceSource, group?.type),
    [group?.devices, group?.type, deviceSource],
  );
  // Compute bounds to frame all group devices on the map.
  const mapBounds = useMemo(() => getGroupMapBounds(groupDevices), [groupDevices]);

  const [sending, setSending] = useState(false);
  // Mike, 2026-08-28: "the test dummy map is super short and needs to be able to
  // be adjustable like the siren command modal" — drag to resize, remembered per browser.
  const [mapPanelHeight, setMapPanelHeight] = useState(() =>
    clampPanelHeight(readStoredPanelHeight(MAP_PANEL_HEIGHT_KEY, MAP_PANEL_DEFAULT_PX)),
  );
  const resizeMapPanel = (wantedPx) => {
    const next = clampPanelHeight(wantedPx);
    setMapPanelHeight(next);
    storePanelHeight(MAP_PANEL_HEIGHT_KEY, next);
  };
  // Per-siren audio overrides for the group trigger: { [deviceId]: audio | null }.
  // Null / absent = the group's file. Sent as `targets` (see buildGroupCommandArgs).
  const [perDeviceAudio, setPerDeviceAudio] = useState({});
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [showProvisionalModal, setShowProvisionalModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Session-wide USGS provisional acceptance — shared with the map and graph views.
  const provisionalAccepted = useSelector(selectUsgsProvisonalAccepted);

  const hasUSGSDevices = useMemo(
    () => groupDevices.some((d) => d?.system_type === 'USGS'),
    [groupDevices],
  );

  // Auto-show the disclaimer the first time a USGS group is opened in this session.
  // Once accepted (provisionalAccepted = true), the auto-show is suppressed for the
  // rest of the session. The info icon always re-opens it manually.
  useEffect(() => {
    if (!show) {
      setShowProvisionalModal(false);
      setShowHistory(false);
      return;
    }
    if (hasUSGSDevices && !provisionalAccepted) {
      setShowProvisionalModal(true);
    }
  }, [show, hasUSGSDevices, provisionalAccepted]);

  // Only allow macro controls when the mix is compatible per rules and has a control layout.
  const macroEligible =
    groupType &&
    layout[groupType.toLowerCase()] &&
    groupDevices.length > 0 &&
    groupDevices.every((device) => isDeviceTypeAllowed(device?.device_type, groupType));

  // History reads live members, independently of the command's synthetic status row.
  const groupHistoryData = useMemo(() => buildGroupHistoryRows(groupDevices), [groupDevices]);
  const commandSeed = useMemo(
    () =>
      macroEligible ? buildGroupCommandDraft(groupDevices[0], groupType, groupHistoryData) : null,
    [groupDevices, groupType, macroEligible, groupHistoryData],
  );
  const [commandDraft, setCommandDraft, startCommandSend] = useGroupCommandDraft(
    group?.id,
    commandSeed,
  );

  // Reset device-level selection when the modal closes or the group changes.
  useEffect(() => {
    setSelectedDeviceId(null);
    setPerDeviceAudio({});
  }, [group?.id, show]);

  // Clear stale device selections when membership changes.
  useEffect(() => {
    if (!groupDevices.length) {
      setSelectedDeviceId(null);
      return;
    }
    if (
      selectedDeviceId != null &&
      !groupDevices.some((device) => device.id === selectedDeviceId)
    ) {
      setSelectedDeviceId(null);
    }
  }, [groupDevices, selectedDeviceId]);

  // Selected device drives the per-device history view.
  const selectedDevice = useMemo(
    () => groupDevices.find((device) => device.id === selectedDeviceId),
    [groupDevices, selectedDeviceId],
  );
  const historyData = useMemo(
    () =>
      selectedDevice != null
        ? buildGroupHistoryRows([selectedDevice], { includeStage: true })
        : groupHistoryData,
    [selectedDevice, groupHistoryData],
  );
  const historyTitle =
    selectedDevice != null
      ? `Device History - ${selectedDevice?.alias || selectedDevice?.name || selectedDevice?.node_id || 'Unknown Device'}`
      : `Device History - ${group?.name || 'Group'}`;
  const historyDeviceType = selectedDevice?.device_type || commandDraft?.device_type || groupType;
  const historySubTitle = undefined;

  // Route to the correct settings modal per device type.
  const onDeviceSettingsOpen = useCallback(
    (event, device) => {
      event.stopPropagation();
      if (DeviceIsSystemType(device, 'Flood')) {
        dispatch(openStageSettings(device.id));
      } else if (device?.system_type === 'USGS') {
        dispatch(openUSGSSettings(device.id));
      } else {
        dispatch(openDeviceSettings(device.id));
      }
    },
    [dispatch],
  );

  // Group controls need SendDeviceGroupCommands (its own permission since 2026-08-28);
  // per-device settings below keep the device's Create-Edit key (canEditDevice).
  const canEditGroupDevices = useMemo(() => {
    if (!groupDevices.length) return false;
    return perms.testPermission([], ['SendDeviceGroupCommands']);
  }, [groupDevices, perms]);

  const canEditDevice = useCallback(
    (device) => hasDevicePermissionFromDevice(perms, device, 'createEdit'),
    [perms],
  );

  const onGroupControlEvent = async (eventType, deviceState, details) => {
    switch (eventType) {
      case 'update':
        setCommandDraft(deviceState, details?.editedFields);
        return;
      case 'submit':
        if (!group?.id) return;
        if (!canEditGroupDevices) return;
        try {
          setSending(true);
          const finishCommandSend = startCommandSend();
          const statusValue = Number(deviceState?.status ?? 0);
          const commentsValue = deviceState?.comments ?? '';
          const commandArgs = buildGroupCommandArgs(deviceState, perDeviceAudio);

          const response = await sendGroupCommand(
            group.id,
            statusValue,
            commentsValue,
            commandArgs,
          );
          if (response?.status && response.status !== 200) {
            throw new Error(`Send Group Command failed (${response.status})`);
          }
          finishCommandSend();
          setSending(false);
        } catch (error) {
          console.error('Error sending group command:', error);
          setSending(false);
        }
        return;
      case 'cancel-audio':
        if (!group?.id) return;
        try {
          // A maintenance operator's stop command carries the override (see DeviceControls).
          if (deviceState?.maintenanceOverride === true) {
            await sendGroupCommand(group.id, 7, '', { maintenanceOverride: true });
          } else {
            await sendGroupCommand(group.id, 7, '');
          }
        } catch (error) {
          console.error('Error sending group command:', error);
        }
        return;
      default:
        return;
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'alias',
        header: 'Device',
        Cell: ({ row }) => (
          <div className="d-flex flex-column">
            <span className="fw-semibold text-break">{row.original.alias}</span>
            <span className="text-secondary text-break">{row.original.name}</span>
          </div>
        ),
      },
      {
        accessorKey: 'node_id',
        header: 'ID',
        Cell: ({ cell }) => <span>{cell.getValue()}</span>,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        Cell: ({ row }) => <NodeStatus device={row.original} />,
      },
      {
        id: 'settings',
        header: '',
        size: 60,
        minSize: 48,
        maxSize: 80,
        grow: false,
        Cell: ({ row }) => {
          const device = row.original;
          if (!canEditDevice(device)) return null;
          return (
            <button
              type="button"
              className="btn btn-link p-0 text-white"
              onClick={(event) => onDeviceSettingsOpen(event, device)}
            >
              <FaCog size={24} />
            </button>
          );
        },
      },
    ],
    [canEditDevice, onDeviceSettingsOpen],
  );

  return (
    <BaseModal
      show={show}
      handleShow={onClose}
      title={group?.name || 'Group Settings'}
      size="xl"
      scrollable
      bodyClassName="min-h-0"
      contentStyle={{
        maxHeight: 'calc(100vh - 8rem)',
        overflowX: 'hidden',
        overflowY: 'auto',
      }}
    >
      {/* flex-shrink-0: the scrollable modal body must scroll, not compress this column —
          compressed, the map collapses (its panes are absolutely positioned, so its
          min-content height is 0) and the history accordion paints over the device list. */}
      <div
        className="d-flex flex-column gap-3 flex-shrink-0"
        style={{ minHeight: 'min(75vh, calc(100vh - 12rem))' }}
      >
        <div
          className="w-100 overflow-hidden rounded-3 flex-shrink-0"
          style={{ height: `${mapPanelHeight}px` }}
        >
          <DeviceMap mode="group" devicesOverride={groupDevices} mapBoundsOverride={mapBounds} />
        </div>
        <DragResizeHandle
          ariaLabel="Resize the group map"
          value={mapPanelHeight}
          onChange={resizeMapPanel}
          onReset={() => resizeMapPanel(MAP_PANEL_DEFAULT_PX)}
        />
        <div className="d-flex flex-column">
          {macroEligible && canEditGroupDevices && commandDraft ? (
            <>
              <DeviceControls
                key={group?.id}
                device={commandDraft}
                devices={groupDevices}
                sendsGroupCommand
                on={onGroupControlEvent}
                sending={sending}
                audioManagerButtonEnabled={false}
                // Every siren has its own file below → the group-wide file is not required.
                audioRequired={
                  !groupDevices.filter(isSirenDevice).every((d) => perDeviceAudio[d.id])
                }
              />
              {/* Per-siren audio: the same file for each, or a separate one where the siren has it. */}
              <div className="mt-2">
                <GroupSirenAudioRows
                  devices={groupDevices}
                  groupAudio={commandDraft?.audio || null}
                  value={perDeviceAudio}
                  disabled={sending}
                  onChange={(deviceId, audio) =>
                    setPerDeviceAudio((prev) => {
                      const next = { ...prev };
                      if (audio) next[deviceId] = audio;
                      else delete next[deviceId];
                      return next;
                    })
                  }
                />
              </div>
            </>
          ) : (
            <div className="bg-primary rounded-3 p-3">
              <h5 className="mb-2">Group Controls</h5>
              <p className="m-0 text-secondary">
                {!macroEligible
                  ? 'This group is not eligible for group controls. Use per-device settings instead.'
                  : 'You do not have permission to apply group controls to this group.'}
              </p>
            </div>
          )}
        </div>

        <div className="d-flex flex-column gap-2 flex-grow-1 min-h-0">
          <div className="d-flex align-items-center gap-2">
            <div className="fw-semibold">Devices in Group</div>
            {hasUSGSDevices && (
              <div className="d-flex">
                <IoMdInformationCircleOutline
                  className="rounded-circle bg-primary p-2 cursor-pointer"
                  style={{ height: 35, width: 35 }}
                  onClick={() => setShowProvisionalModal(true)}
                />
                <span className="align-self-center text-white-50 small ps-3">
                  USGS data may be{' '}
                  <span
                    className="text-decoration-underline cursor-pointer text-white"
                    onClick={() => setShowProvisionalModal(true)}
                  >
                    provisional
                  </span>
                  .
                </span>
              </div>
            )}
          </div>
          <div className="flex-grow-1 min-h-0 overflow-auto">
            <div className="d-none d-lg-block">
              <GroupDevicesTable
                groupDevices={groupDevices}
                columns={columns}
                selectedDeviceId={selectedDeviceId}
                onSelectDevice={setSelectedDeviceId}
              />
            </div>
            <div className="d-block d-lg-none">
              <GroupDevicesAccordion
                groupDevices={groupDevices}
                selectedDeviceId={selectedDeviceId}
                onSelectDevice={setSelectedDeviceId}
                onDeviceSettingsOpen={onDeviceSettingsOpen}
                canEditDevice={canEditDevice}
              />
            </div>
          </div>
        </div>

        {historyData && (
          <Accordion
            activeKey={showHistory ? 'history' : null}
            onSelect={() => setShowHistory((prev) => !prev)}
          >
            <Accordion.Item eventKey="history" className="rounded-3 overflow-hidden border-0">
              <Accordion.Header>{historyTitle}</Accordion.Header>
              <Accordion.Body className="p-0">
                <div className="w-100 fs-6">
                  <DeviceHistory
                    title={historyTitle}
                    subTitle={historySubTitle}
                    data={historyData}
                    deviceType={historyDeviceType}
                    hideHeader
                    loading={historyLoading}
                    hasMore={historyHasMore}
                    onLoadMore={loadMoreHistory}
                    error={historyError}
                  />
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        )}
      </div>

      {hasUSGSDevices && (
        <ConfirmModal
          open={showProvisionalModal}
          setOpen={setShowProvisionalModal}
          options={['Continue']}
          callbacks={[
            () => {
              dispatch(setUsgsProvisonalAccepted(true));
              setShowProvisionalModal(false);
            },
          ]}
          message={USGS_PROVISIONAL_STATEMENT}
          closeAction={() => {}}
          size="lg"
        />
      )}
    </BaseModal>
  );
};

export default GroupDeviceSettingsModal;
